/**
 * PlotNode — custom React Flow node for saved plots on the DAG.
 * Renders a mini version of the actual chart when value_json has config + data_snapshot.
 */

import { memo, useMemo } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Tooltip } from "@radix-ui/themes";
import type { DAGNode } from "../../api/artifacts";
import { plotTooltipContent } from "./dagTooltips";
import { useTheme } from "../../context/ThemeContext";
import { getRenderer } from "../plot-builder/renderers";
import {
  createDefaultConfig,
  getDefaultEngine,
  type PlotBuilderConfig,
  type ChartType,
} from "../plot-builder/types";
import { CHART_TYPE_ICONS } from "../plot-builder/types";
import { PLOT_NODE_HEIGHT, PLOT_NODE_WIDTH, PLOT_NODE_TITLE_HEIGHT } from "./dagNodeSizing";
import { CrispNodeContent } from "./CrispNodeContent";

const CHART_HEIGHT = PLOT_NODE_HEIGHT - PLOT_NODE_TITLE_HEIGHT;

interface SavedPlotValueJson {
  title?: string;
  chart_type?: string;
  config?: Record<string, unknown>;
  data_snapshot?: Record<string, unknown>[];
}

export const PlotNode = memo(function PlotNode({ data }: NodeProps) {
  const { theme } = useTheme();
  const isLight = theme === "light";
  const dagNode = data.dagNode as DAGNode | undefined;
  const valueJson = (data.dagNode as Record<string, unknown>)?.value_json as
    | SavedPlotValueJson
    | undefined;

  const tooltipText = useMemo(() => dagNode ? plotTooltipContent(dagNode) : String(data.label ?? "Saved Plot"), [dagNode, data.label]);

  const { canRender, config, plotData, chartType, rowCount } = useMemo(() => {
    if (!valueJson?.config || !Array.isArray(valueJson.data_snapshot)) {
      const ct = (valueJson?.chart_type as ChartType) ?? "line";
      return { canRender: false, config: null, plotData: [], chartType: ct, rowCount: 0 };
    }
    const snapshot = valueJson.data_snapshot;
    const rawConfig = valueJson.config as Record<string, unknown> | undefined;
    const chartTypeVal = (valueJson.chart_type as ChartType) ?? "line";
    const engine = (rawConfig?.engine as PlotBuilderConfig["engine"]) ?? getDefaultEngine(chartTypeVal);
    const config: PlotBuilderConfig = {
      ...createDefaultConfig(),
      ...rawConfig,
      chartType: chartTypeVal,
      engine,
      title: "",
      showLegend: false,
      plotPadding: 8,
      titleSize: 10,
    } as PlotBuilderConfig;
    const hasAxes =
      config.xAxis?.field &&
      config.yAxis?.field &&
      snapshot.some(
        (row) =>
          row[config.xAxis!.field] != null &&
          row[config.yAxis!.field] != null
      );
    return {
      canRender: hasAxes && snapshot.length > 0,
      config,
      plotData: snapshot,
      chartType: chartTypeVal,
      rowCount: snapshot.length,
    };
  }, [valueJson]);

  const Renderer = useMemo(
    () => (config ? getRenderer(config.engine) : null),
    [config?.engine]
  );

  const icon = CHART_TYPE_ICONS[chartType] ?? "📊";
  const title = String(data.label ?? valueJson?.title ?? "Saved Plot");
  const isSelected = !!data.selected;

  return (
    <>
    <Handle type="target" position={Position.Top} style={{ visibility: "hidden" }} />
    <CrispNodeContent width={PLOT_NODE_WIDTH} height={PLOT_NODE_HEIGHT}>
    <Tooltip content={<span style={{ whiteSpace: "pre-line" }}>{tooltipText}</span>} delayDuration={400}>
    <div
      style={{
        background: isLight ? "#fff" : "#1c211e",
        color: isLight ? "#111" : "#f0f2f0",
        borderRadius: 10,
        overflow: "hidden",
        border: isSelected
          ? "2px solid #f59e0b"
          : `1px solid ${isLight ? "#e2c87a" : "#6b5c28"}`,
        cursor: "pointer",
        fontFamily: "var(--fc-font-family)",
        width: PLOT_NODE_WIDTH,
        height: PLOT_NODE_HEIGHT,
        boxSizing: "border-box",
        boxShadow: isSelected
          ? "0 0 0 2px rgba(245,158,11,0.25), 0 4px 12px rgba(0,0,0,0.15)"
          : `0 2px 8px ${isLight ? "rgba(0,0,0,0.08)" : "rgba(0,0,0,0.3)"}`,
        transition: "box-shadow 0.2s, border-color 0.2s",
      }}
    >

      {/* Title bar */}
      <div
        style={{
          height: PLOT_NODE_TITLE_HEIGHT,
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "0 8px",
          background: isLight
            ? "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)"
            : "linear-gradient(135deg, #2a2510 0%, #332d14 100%)",
          borderBottom: `1px solid ${isLight ? "#e2c87a" : "#3d3518"}`,
          fontSize: 10,
          fontWeight: 600,
          lineHeight: 1,
        }}
      >
        <span style={{ fontSize: 11, flexShrink: 0 }}>{icon}</span>
        <span
          style={{
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            opacity: 0.85,
          }}
        >
          {title}
        </span>
        {rowCount > 0 && (
          <span
            style={{
              fontSize: 9,
              opacity: 0.5,
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            {rowCount}
          </span>
        )}
      </div>

      {/* Chart area */}
      {canRender && config && Renderer ? (
        <div
          style={{
            width: PLOT_NODE_WIDTH,
            height: CHART_HEIGHT,
            minHeight: CHART_HEIGHT,
            position: "relative",
          }}
        >
          <Renderer
            data={plotData}
            config={config}
            theme={theme}
            mini
            width={PLOT_NODE_WIDTH}
            height={CHART_HEIGHT}
          />
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            fontSize: 12,
            fontWeight: 600,
            height: CHART_HEIGHT,
            boxSizing: "border-box",
            opacity: 0.4,
          }}
        >
          <span style={{ fontSize: 20 }}>{icon}</span>
          <span>No preview</span>
        </div>
      )}
    </div>
    </Tooltip>
    </CrispNodeContent>
    <Handle type="source" position={Position.Bottom} style={{ visibility: "hidden" }} />
    </>
  );
});
